import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ArrowLeft,
  Download
} from 'lucide-react';
import { ImportType } from './ImportTypeSelector';

interface FileUploadProps {
  importType: ImportType;
  organizationId: string;
  onFileUploaded: (file: File, analysis: any) => void;
  onBack: () => void;
}

interface UploadState {
  status: 'idle' | 'uploading' | 'analyzing' | 'success' | 'error';
  progress: number;
  file?: File;
  error?: string;
  analysis?: any;
}

const importTypeConfig = {
  students: {
    title: 'Student Import',
    description: 'Upload a CSV file containing student information',
    sampleColumns: ['First Name', 'Last Name', 'Date of Birth', 'Grade/Age', 'Parent Email'],
    requirements: [
      'First name and last name are required',
      'At least one parent email or phone number',
      'Date format: MM/DD/YYYY or YYYY-MM-DD'
    ]
  },
  families: {
    title: 'Family Import',
    description: 'Upload a CSV file containing family and parent information',
    sampleColumns: ['Family Name', 'Parent 1 Name', 'Parent 1 Email', 'Phone', 'Address'],
    requirements: [
      'Family name or parent name required',
      'At least one contact method (email or phone)',
      'Address fields can be combined or separate'
    ]
  },
  classes: {
    title: 'Class Import',
    description: 'Upload a CSV file containing class and program information',
    sampleColumns: ['Class Name', 'Instructor', 'Day/Time', 'Capacity', 'Age Group'],
    requirements: [
      'Class name is required',
      'Schedule information (day and time)',
      'Capacity should be a number'
    ]
  },
  enrollments: {
    title: 'Enrollment Import',
    description: 'Upload a CSV file containing student enrollments',
    sampleColumns: ['Student Name', 'Class Name', 'Enrollment Date', 'Status'],
    requirements: [
      'Student and class must exist in system',
      'Valid enrollment status (Active, Waitlist, etc.)',
      'Date format: MM/DD/YYYY or YYYY-MM-DD'
    ]
  },
  payments: {
    title: 'Payment Import',
    description: 'Upload a CSV file containing payment history',
    sampleColumns: ['Family Name', 'Amount', 'Date', 'Payment Method', 'Status'],
    requirements: [
      'Family must exist in system',
      'Amount should be in decimal format (e.g., 125.00)',
      'Valid payment status (Paid, Pending, Failed)'
    ]
  },
  staff: {
    title: 'Staff Import',
    description: 'Upload a CSV file containing staff and instructor information',
    sampleColumns: ['Name', 'Email', 'Phone', 'Role', 'Specialties'],
    requirements: [
      'Name and email are required',
      'Valid email format',
      'Role should match your staff roles'
    ]
  }
};

export function FileUpload({ importType, organizationId, onFileUploaded, onBack }: FileUploadProps) {
  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: 0
  });

  const config = importTypeConfig[importType];

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file) return;

    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type)) {
      setUploadState({
        status: 'error',
        progress: 0,
        error: 'Please upload a CSV or Excel file (.csv, .xls, .xlsx)'
      });
      return;
    }

    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setUploadState({
        status: 'error',
        progress: 0,
        error: 'File is too large. Maximum size is 50MB.'
      });
      return;
    }

    setUploadState({
      status: 'uploading',
      progress: 0,
      file
    });

    try {
      // Simulate upload progress
      for (let i = 0; i <= 50; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadState(prev => ({ ...prev, progress: i }));
      }

      // Call the actual Cloudflare Worker API
      setUploadState(prev => ({
        ...prev,
        status: 'analyzing',
        progress: 60
      }));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('importType', importType);
      formData.append('organizationId', organizationId);

      const response = await fetch('https://app.hivetivity.com/api/import/analyze', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const workerAnalysis = await response.json();
      
      // Use the real Worker analysis data
      const realAnalysis = {
        analysisId: workerAnalysis.analysisId, // Include the analysis ID
        headers: workerAnalysis.headers || [], // Real headers from CSV
        totalRows: workerAnalysis.dataQuality?.totalRows || 0,
        detectedFormat: 'CSV',
        confidence: workerAnalysis.sourceDetected?.confidence || 0.5,
        suggestedMappings: Object.entries(workerAnalysis.mappings || {}).reduce((acc, [header, mapping]: [string, any]) => {
          acc[header] = {
            field: mapping.targetField,
            confidence: mapping.confidence
          };
          return acc;
        }, {} as Record<string, any>),
        // Pass through real data for FieldMapping
        workerResults: workerAnalysis
      };

      setUploadState({
        status: 'success',
        progress: 100,
        file,
        analysis: realAnalysis
      });

      onFileUploaded(file, realAnalysis);

    } catch (error) {
      setUploadState({
        status: 'error',
        progress: 0,
        error: error instanceof Error ? error.message : 'Upload failed'
      });
    }
  }, [onFileUploaded]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const downloadTemplate = () => {
    // Create a sample CSV template based on import type
    const headers = config.sampleColumns;
    const sampleRow = headers.map(header => `Sample ${header}`);
    
    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${importType}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{config.title}</h2>
          <p className="text-gray-600">{config.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload Area */}
        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              {uploadState.status === 'idle' && (
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Drop your file here or click to browse
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Supports CSV and Excel files up to 50MB
                  </p>
                  <Button>
                    Select File
                  </Button>
                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    className="hidden"
                  />
                </div>
              )}

              {(uploadState.status === 'uploading' || uploadState.status === 'analyzing') && (
                <div className="text-center space-y-4">
                  <FileText className="w-12 h-12 text-blue-500 mx-auto" />
                  <div>
                    <h3 className="text-lg font-medium">
                      {uploadState.status === 'uploading' ? 'Uploading file...' : 'Analyzing data...'}
                    </h3>
                    <p className="text-gray-600">
                      {uploadState.status === 'uploading' 
                        ? 'Please wait while we upload your file'
                        : 'Our AI is examining your data structure'
                      }
                    </p>
                  </div>
                  <div className="w-full max-w-sm mx-auto">
                    <Progress value={uploadState.progress} className="w-full" />
                    <p className="text-sm text-gray-500 mt-2">{uploadState.progress}% complete</p>
                  </div>
                </div>
              )}

              {uploadState.status === 'success' && uploadState.analysis && (
                <div className="text-center space-y-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                  <div>
                    <h3 className="text-lg font-medium text-green-900">Analysis Complete!</h3>
                    <p className="text-gray-600">
                      Found {uploadState.analysis.totalRows} rows with {uploadState.analysis.headers.length} columns
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-green-800 mb-2">Detected columns:</p>
                    <div className="flex flex-wrap gap-2">
                      {uploadState.analysis.headers.map((header: string, index: number) => (
                        <span key={index} className="px-2 py-1 bg-white rounded text-xs text-green-700 border border-green-200">
                          {header}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {uploadState.status === 'error' && (
                <div className="text-center space-y-4">
                  <XCircle className="w-12 h-12 text-red-500 mx-auto" />
                  <div>
                    <h3 className="text-lg font-medium text-red-900">Upload Failed</h3>
                    <p className="text-red-600">{uploadState.error}</p>
                  </div>
                  <Button 
                    onClick={() => setUploadState({ status: 'idle', progress: 0 })}
                    variant="outline"
                  >
                    Try Again
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Help */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expected Format</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Sample columns:</p>
                <div className="space-y-1">
                  {config.sampleColumns.map((column, index) => (
                    <div key={index} className="px-2 py-1 bg-gray-100 rounded text-xs">
                      {column}
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm" 
                onClick={downloadTemplate}
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {config.requirements.map((req, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Don't worry if your columns don't match exactly. We'll help you map them in the next step.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
}